import { authStateAtom, useIsAuthenticated } from '@/auth/authStateAtom';
import { clearAuthenticationState } from '@/auth/clearAuthState';
import { useLogoutOnServer } from '@/fetching/api/auth';
import { useAxleQuery } from '@/fetching/apiHelpers';
import { queryClient } from '@/fetching/queryClient';
import { OrganizationBasic } from '@/fetching/sharedSchemas/basicSchemas';
import { useClearLastAuthedPage } from '@/utils/lastAuthedPage';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

//////////////////////////////////////////////////////////////////////////////
// region Private atom.
//////////////////////////////////////////////////////////////////////////////

// The atom itself lives in authStateAtom.ts (so non-React code like the Apollo
//  error link can clear it via clearAuthState.ts), but it is deliberately not
//  re-exported from here: code should only ever modify it using the setters below.

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Getters.
//////////////////////////////////////////////////////////////////////////////

export const useAuthStateValue = () => useAtomValue(authStateAtom);
export const useSetAuthState = () => useSetAtom(authStateAtom);

// Defined alongside the atom, and re-exported here so that this module stays the
//  one place app code reaches for authentication state.
export { useIsAuthenticated };

type AuthenticatedUser = {
    uuid?: string;
    email: string;
    organization: OrganizationBasic;
    firstName?: string;
    lastName?: string;
    pylonEmail?: string;
    pylonEmailHash?: string;
};

// Use this (instead of useSetAuthState) when signing a user IN.
//
// It wipes the React Query cache BEFORE flipping the auth atom. This matters
//  because a 401 — e.g. the one fired when an authenticated query refetches
//  during a logout, or a real mid-session expiry — is parked by the axios
//  response interceptor as a promise that never resolves, leaving a "zombie"
//  query stuck in a fetching state in the cache (see clearAuthState.ts and
//  axiosClient.tsx). If we re-entered the app without clearing, a consumer of
//  that query key (e.g. useAppConfig() in the nav drawer) would re-subscribe to
//  the zombie and suspend forever. Clearing here — while we are still on the
//  query-less login page, so there are no authenticated observers to refetch —
//  guarantees the new session's pages mount against a clean cache and a live
//  session. (Logout also clears the cache via clearAuthState.ts to drop PHI
//  immediately; clearing again here is what neutralizes any zombie query, since
//  the logout-time clear can itself spawn one.)
export const useEnterAuthenticatedSession = () => {
    const setAuthState = useSetAtom(authStateAtom);
    return useCallback(
        (user: AuthenticatedUser) => {
            queryClient.clear();
            setAuthState(user);
        },
        [setAuthState],
    );
};

// This returns null if the user is not logged in.
export const useUserEmailIfLoggedIn = (): string | null => {
    const authentication = useAtomValue(authStateAtom);
    return authentication?.email ?? null;
};

export const useUserEmails = (): {
    currentUserEmail: string | null;
    spoofingUserEmail: string | null;
} => {
    const email = useUserEmailIfLoggedIn();
    if (!email) {
        return {
            spoofingUserEmail: null,
            currentUserEmail: null,
        };
    }
    const segments = email.split('/');
    if (segments.length === 2) {
        return {
            currentUserEmail: segments[1]?.trim() ?? email,
            spoofingUserEmail: segments[0]?.trim() ?? email,
        };
    }

    return {
        currentUserEmail: email,
        spoofingUserEmail: null,
    };
};

// This assumes the user is logged in, otherwise it errors out.
export const useUserEmail = (): string | null => {
    const authState = useAtomValue(authStateAtom);
    if (!authState) {
        throw new Error('User is not authenticated.');
    }
    return authState.email;
};

// This assumes the user is logged in, otherwise it errors out.
export const useUserOrg = (): OrganizationBasic => {
    const authState = useAtomValue(authStateAtom);
    if (!authState) {
        throw new Error('User is not authenticated.');
    }
    return authState.organization;
};

// Returns the organization ID of the logged-in user.
// This assumes the user is logged in, otherwise it errors out.
// This is especially useful when deciding whether to show the "Organization"
//  column in tables. You can hide the organization column if all the rows'
//  organizations are the same as the logged-in user's organization.
export const useUserOrgId = (): string => {
    const userOrg = useUserOrg();
    return userOrg.uuid;
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Setters.
//////////////////////////////////////////////////////////////////////////////

// Clears the local authentication state.
// If you want to end a user’s session on the backend, you also have to send
//  a logout request to the backend.
// This is a thin hook wrapper around the module-level clearAuthenticationState()
//  (see clearAuthState.ts for the details of what gets cleared and why).
export const useClearAuthenticationState = () => {
    return useCallback(() => clearAuthenticationState(), []);
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Miscellaneous stuff.
//////////////////////////////////////////////////////////////////////////////

// This is currently not used anywhere. It's here as a reference
//  for potential use in the future.
//
// This hook returns true once the user is logged in, and we have confirmed we
//  can make an authenticated query successfully. This is safe to call even
//  when the user is logged out, or improperly logged in.
export const useIsAuthenticatedRobust = (): boolean => {
    const isAuthenticated = useIsAuthenticated();
    return useAxleQuery({
        path: '/app-config',
        suspense: false,
        // We won't even try running the query until the local authentication state is set:
        enabled: isAuthenticated,
    }).isReady;
};

// endregion

export const useLogout = (): (() => void) => {
    const logoutOnServer = useLogoutOnServer();
    const clearAuthenticationState = useClearAuthenticationState();
    const clearLastAuthedPage = useClearLastAuthedPage();

    return useCallback(() => {
        logoutOnServer({})
            .catch(reportError)
            .finally(() => {
                clearAuthenticationState();

                // Signing out on purpose ends the session rather than interrupting it,
                //  so there is no page to come back to: signing back in lands on the
                //  landing page.
                clearLastAuthedPage();
            });
    }, [logoutOnServer, clearAuthenticationState, clearLastAuthedPage]);
};
